// How often and when, in the report's own time zone. Times stay "HH:mm" strings; the service computes
// every date, so no daylight-saving maths happens here.
import React, { useMemo } from "react";
import { Autocomplete, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { DAYS, scheduleNotes } from "utils/reportDefinition";
import { fieldError } from "utils/reportErrors";
import { timeZoneOptions, zoneLabel } from "utils/timezone";

const INTERVALS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

function Picker({ label, value, onChange, width, children }) {
  return (
    <FormControl size="small" style={{ width }}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        {children}
      </Select>
    </FormControl>
  );
}

export default function SchedulePicker({ schedule, onChange, limits, errors }) {
  const set = (patch) => onChange({ ...schedule, ...patch });
  const zones = useMemo(() => timeZoneOptions(), []);
  const zoneValue = zones.find((z) => z.value === schedule.timeZone) || { value: schedule.timeZone, label: zoneLabel(schedule.timeZone) };
  const dayRange = (limits && limits.dayOfMonth) || { min: 1, max: 28 };
  const days = [];
  for (let d = dayRange.min; d <= dayRange.max; d += 1) days.push(d);

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "flex-start" }}>
        <Picker label="How often" value={schedule.interval} onChange={(interval) => set({ interval })} width="140px">
          {INTERVALS.map((i) => (
            <MenuItem key={i.value} value={i.value}>
              {i.label}
            </MenuItem>
          ))}
        </Picker>
        {schedule.interval === "weekly" ? (
          <Picker label="Day" value={schedule.dayOfWeek} onChange={(dayOfWeek) => set({ dayOfWeek })} width="150px">
            {DAYS.map((day, i) => (
              <MenuItem key={day} value={i + 1}>
                {day}
              </MenuItem>
            ))}
          </Picker>
        ) : null}
        {schedule.interval === "monthly" ? (
          <Picker label="Day of the month" value={schedule.dayOfMonth} onChange={(dayOfMonth) => set({ dayOfMonth })} width="150px">
            {days.map((d) => (
              <MenuItem key={d} value={d}>
                {d}
              </MenuItem>
            ))}
          </Picker>
        ) : null}
        <TextField
          size="small"
          type="time"
          label="Send time"
          value={schedule.sendAt}
          onChange={(e) => set({ sendAt: e.target.value })}
          error={!!fieldError(errors, "schedule.sendAt")}
          helperText={fieldError(errors, "schedule.sendAt")}
          InputLabelProps={{ shrink: true }}
          style={{ width: "130px" }}
        />
        <Autocomplete
          size="small"
          disableClearable
          options={zones}
          value={zoneValue}
          getOptionLabel={(z) => z.label || ""}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          onChange={(e, zone) => set({ timeZone: zone.value })}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Time zone"
              error={!!fieldError(errors, "schedule.timeZone")}
              helperText={fieldError(errors, "schedule.timeZone")}
            />
          )}
          style={{ width: "340px" }}
        />
      </div>
      {scheduleNotes(schedule).map((text) => (
        <p key={text} style={{ fontSize: "13px", color: "grey", margin: "8px 0 0 0" }}>
          {text}
        </p>
      ))}
    </div>
  );
}
